<template>
  <div class="companies">
    <SectionHeader :title="$t('meta.sensors')">
      <template v-slot:meta>
        <a v-if="csvLoading" class="button button-grey button-round button-small material-icons csv-loading csv-button"
          >hourglass_top</a
        >
        <a v-else @click="downloadCsv" class="button button-green button-round button-small material-icons csv-button"
          >file_download</a
        >
        <Link
          :to="`/company/sensors/import`"
          class="button button-green button-round button-small material-icons csv-button"
          >file_upload</Link
        >
        <Link
          :to="`/company/sensors/new`"
          class="button button-green button-round button-small"
          style="display: inline-block; position: relative; top: -3px"
          >{{ $t('sensors.addSensor') }}</Link
        >
      </template>
    </SectionHeader>

    <div class="body-content cf">
      <CompanyNav />
      <div class="body">
        <SearchField
          :label="$t('meta.search')"
          :name="'sensor-search-input'"
          :placeholder="$t('sensors.searchPlaceholder')"
          v-model="searchPhrase"
          @input="searchHandler"
        />
        <LoadingSpinner v-if="loading" />
        <div v-else-if="sensors && sensors.length === 0" class="alert alert-no-entry">
          {{ $t('sensors.noSensors') }}
        </div>
        <template v-else>
          <LinkList>
            <LinkListItem
              v-for="sensor in sensors"
              :key="sensor.uuid"
              :primaryLink="`/company/sensors/${sensor.uuid}`"
              alwaysRenderSecondaryLink
              :secondaryLink="sensor.container ? `/company/containers/${sensor.container.uuid}` : 'null'"
            >
              <template #main-content>
                <span>{{ sensor.name || '' }}</span>
                <span>{{ sensor.hardware_id }}</span>
                <span>{{ sensorTypes.find(type => type.value == sensor.sensor_type).label }}</span>
              </template>
              <template #secondary-content v-if="sensor.container">
                <span>{{ sensor.container.name }}</span>
              </template>
              <template #action-content>
                <Link :to="`/company/sensors/${sensor.uuid}`" class="link-list-item__action"
                  ><span class="material-icons">edit</span></Link
                >
                <button @click="deleteSensor(sensor.uuid)" class="link-list-item__action">
                  <span class="material-icons">delete</span>
                </button>
              </template>
            </LinkListItem>
          </LinkList>
          <pagination
            v-if="pagination"
            v-model="page"
            :records="pagination.total_objects"
            :per-page="pagination.per_page"
            :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
            @paginate="getSensors"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import Pagination from 'vue-pagination-2'

import SearchField from '@/components/Form/Search/SearchField'
import LinkListItem from '@/components/LinkList/LinkListItem.vue'
import LinkList from '@/components/LinkList/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner'
import SectionHeader from '@/components/SectionHeader'
import CompanyNav from '@/components/company_nav'

import exportsApi from '@/api/exports'
import sensorsApi from '@/api/sensors'
import { SEARCH_INPUT_DELAY } from '@/utils/searchInputDelay'

export default {
  name: 'company_sensors',
  inject: ['companyUser', 'showPopup'],
  props: {
    searchInputDelay: {
      type: Number,
      default: SEARCH_INPUT_DELAY,
    },
  },
  data: () => ({
    csvLoading: false,
    loading: true,
    pagination: null,
    page: 1,
    searchPhrase: '',
    sensors: [],
    sensorTypes: [],
  }),
  components: {
    CompanyNav,
    SectionHeader,
    SearchField,
    LinkList,
    LinkListItem,
    LoadingSpinner,
    Pagination,
  },
  computed: {
    searchHandler() {
      return debounce(
        () => {
          // The resulting records of the search cause a new pagination
          // and we start with page 1 for showing the results.
          this.getSensors(1, false)
        },
        this.searchInputDelay, // Limit search API calls to once per 400ms while typing
        { trailing: true },
      )
    },
  },
  methods: {
    async getSensors(page = null, showLoadingSpinner = true) {
      if (showLoadingSpinner) this.loading = true
      if (page) this.page = page

      const params = {
        company_uuid: this.companyUser.company_uuid,
        page: this.page,
        search_phrase: this.searchPhrase || null, // nullifies the empty string
      }

      const response = await sensorsApi.getAll(params)

      this.sensors = response.sensors
      this.pagination = response.pagination

      this.loading = false
    },
    async downloadCsv() {
      this.csvLoading = true
      await exportsApi.create({ type: 'sensors_csv', company_uuid: this.companyUser.company_uuid })
      this.csvLoading = false
    },
    async deleteSensor(sensorUuid) {
      const confirmAlert = await this.showPopup({ content: this.$t('sensors.deleteConfirmation'), type: 'confirm' })
      if (!confirmAlert) return null

      const response = await sensorsApi.delete(sensorUuid)
      if (response.success) {
        this.getSensors()
      }
    },
  },
  async mounted() {
    this.sensorTypes = await sensorsApi.selectableSensorTypes()
    this.getSensors(1)
  },
}
</script>

<style lang="scss" scoped>
.csv-button {
  font-size: 20px;
  display: inline-block;
  position: relative;
  top: 2px;
  margin: 0 10px 0 0 !important;
  padding: 0.2rem 1rem;
}
</style>
